import { FormApi } from "final-form";
import { ReactNode, useRef } from "react";

import { DateTime } from "@bps/date-time";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { StashedEncounterClinicalData } from "@stores/clinical/models/StashedEncounterClinicalData.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { SubmissionForm } from "@ui-components/form/submission-form/SubmissionForm.tsx";
import { SubmissionFormProps } from "@ui-components/form/submission-form/SubmissionForm.types.ts";
import { SubmissionFormAutosave } from "@ui-components/form/submission-form/SubmissionFormAutosave.tsx";
import { usePersistedHelper } from "@ui-components/hooks/usePersistedHelper.ts";

import { FormHeaderWrapper } from "./FormHeaderWrapper.tsx";

type ClinicalSubmissionFormProps<FormValues extends object = object> = Omit<
  SubmissionFormProps<FormValues>,
  "onSubmitSucceeded" | "disableRoutePrompt" | "extraPromptCondition"
> & {
  onSubmitSucceeded?: (
    values: FormValues,
    form: FormApi<FormValues>,
    isSaveAndClose?: boolean
  ) => void;
  heading: string;
  onCancel?: (form: FormApi<any, Partial<any>>) => void;
  disableButtonCondition?: (
    stashedData?: StashedEncounterClinicalData
  ) => boolean;
  lastUpdatedDate?: DateTime;
  lastUpdatedUserId?: string;
  extraPromptConditionOnCancel?: (form: FormApi<any, Partial<any>>) => boolean;
  canSubmit?: (form: FormApi<any, Partial<any>>) => Promise<boolean>;
  additionalDisableCheck?: (values: Partial<any>) => boolean;
  clinicalToolAfterSubmit?: (value: boolean) => void;
  submitButtonTextCondition?: (
    stashedData?: StashedEncounterClinicalData
  ) => string;
  noGap?: boolean;
  promptOnCancel?: boolean;
  confidentialShield?: ReactNode;
  onClose?: () => void;
  children?: ReactNode;
  hideSubmit?: boolean;
  hasSeparator?: boolean;
};

export const ClinicalSubmissionFormUnTyped = <
  FormValues extends object = object
>(
  props: ClinicalSubmissionFormProps<FormValues>
) => {
  const {
    heading,
    onCancel,
    disableButtonCondition,
    lastUpdatedDate,
    lastUpdatedUserId,
    extraPromptConditionOnCancel,
    canSubmit,
    clinicalToolAfterSubmit,
    noGap,
    promptOnCancel,
    confidentialShield,
    onClose,
    children,
    submitButtonTextCondition,
    additionalDisableCheck,
    hideSubmit,
    hasSeparator,
    initialValues
  } = props;

  const { core } = useStores();

  const _initialValues = usePersistedHelper(() => initialValues);

  const isSaveAndClose = useRef<boolean>(false);

  const saveAndCloseOnClick = async (form: FormApi) => {
    isSaveAndClose.current = true;
    await form.submit();
  };

  return (
    <SubmissionForm
      {...props}
      initialValues={_initialValues}
      onSubmitSucceeded={(values, form) =>
        props.onSubmitSucceeded &&
        props.onSubmitSucceeded(values, form, isSaveAndClose.current)
      }
      buttonsProps={{
        ...props.buttonsProps,
        onCancel: () => {
          isSaveAndClose.current = false;
        }
      }}
      styles={{
        root: {
          margin: -16
        }
      }}
      autoFocus={false}
      disableRoutePrompt={
        !core.hasPermissions(Permission.ClinicalAutosavePrerel)
      }
      extraPromptCondition={
        core.hasPermissions(Permission.ClinicalAutosavePrerel)
          ? form =>
              !form.getState().valid &&
              "This form has unsaved data.  If you navigate away, all changes will be lost."
          : undefined
      }
      render={() => {
        return (
          <>
            <FormHeaderWrapper
              heading={heading}
              onCancel={onCancel}
              extraPromptConditionOnCancel={extraPromptConditionOnCancel}
              disableButtonCondition={disableButtonCondition}
              lastUpdatedDate={lastUpdatedDate}
              lastUpdatedUserId={lastUpdatedUserId}
              canSubmit={canSubmit}
              clinicalToolAfterSubmit={clinicalToolAfterSubmit}
              noGap={noGap}
              promptOnCancel={promptOnCancel}
              confidentialShield={confidentialShield}
              onClose={onClose}
              saveAndCloseOnClick={saveAndCloseOnClick}
              submitButtonTextCondition={submitButtonTextCondition}
              additionalDisableCheck={additionalDisableCheck}
              hideSubmit={hideSubmit}
              hasSeparator={hasSeparator}
            >
              {children}
            </FormHeaderWrapper>
            <SubmissionFormAutosave />
          </>
        );
      }}
    />
  );
};

export const ClinicalSubmissionForm = <FormValues extends object = object>(
  props: ClinicalSubmissionFormProps<FormValues>
) => (
  <ClinicalSubmissionFormUnTyped
    {...(props as ClinicalSubmissionFormProps<any>)}
  />
);
